<template>
  <div
      class="television-addon-card io-eq-el"
      :class="{
        small,
      }"
  >
    <div
        class="television-addon-card__title"
        data-eq-part="television-addon-card__title"
    >
      <div>
        <img :src="flagUrl" />
      </div>
      <div>
        {{ addon.name }}
      </div>
    </div>
    <div
        class="television-addon-card__benefits"
        data-eq-part="television-addon-card__benefits"
    >
      <List
          type="icon"
          icon="fa-solid fa-check"
          :items="addonProConList"
          :small="small"
      />
    </div>
    <div class="television-addon-card__price" data-eq-part="television-addon-card__price">
      <div>+{{ monthlyBasePrice }}&thinsp;€/Monat</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import List from '@/components/List.vue';
import { formatNumber } from '@/helpers';
import TelevisionPackageInterface from '@/interfaces/TelevisionPackageInterface';
import { computed } from 'vue';

const props = defineProps<{
  addon: TelevisionPackageInterface
  small?: boolean
}>();

const flagUrl = computed(() => `${window.ioappConfig.themeUri}/public/img/flags/${props.addon.id}.svg`);

const monthlyBasePrice = computed(() => formatNumber(props.addon.price?.recurring.find((recurringPrice) => recurringPrice?.type === 'base_price')?.value || 0, 2));

const addonProConList = computed(() => [
  ...(props.addon.benefits || []),
  ...((props.addon.cons || []).map((con: string | string[]) => [
    Array.isArray(con) ? con[0] : con,
    Array.isArray(con) ? con[1] : undefined,
    'fa-solid fa-xmark',
  ])),
]);
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.television-addon-card {
  box-shadow: 0 3px 6px rgba(0, 0, 0, .15);
  border-radius: 20px;
  background-color: #ffffff;

  @media(min-width: map-get($grid-breakpoints, 'lg')) {
    border-radius: 40px;
  }

  &__title {
    display: grid;
    grid-template-columns: auto;
    justify-content: center;
    text-align: center;
    gap: 10px;
    padding-top: 20px;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      padding-top: 30px;
    }

    > div {
      &:nth-child(1) {
        img {
          display: block;
          margin: 0 auto;
          height: 1.75rem;
          width: auto;
          border-radius: 5px;
          box-shadow: 0 0 2px rgba(0, 0, 0, .15);

          @media (min-width: map_get($grid-breakpoints, 'lg')) {
            height: 2.5rem;
          }
        }
      }

      &:nth-child(2) {
        font-size: 1rem;
        font-weight: 700;

        @media(min-width: map-get($grid-breakpoints, 'lg')) {
          font-size: 1.5rem;
        }
      }
    }
  }

  &.small {
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);

    .television-addon-card {
      &__title {
        padding-top: 10px;

        @media(min-width: map-get($grid-breakpoints, 'lg')) {
          padding-top: 20px;
        }

        > div {
          &:nth-child(1) {
            img {
              height: 1.25rem;

              @media (min-width: map_get($grid-breakpoints, 'lg')) {
                height: 1.875rem;
              }
            }
          }

          &:nth-child(2) {
            font-size: 1rem;

            @media (min-width: map_get($grid-breakpoints, 'lg')) {
              font-size: 1.125rem;
            }
          }
        }
      }

      &__benefits {
        @media(min-width: map-get($grid-breakpoints, 'lg')) {
          padding: 20px 30px;
        }
      }

      &__price {
        font-size: 1rem;
        border-radius: 0 0 10px 10px;
        padding: 10px 20px;

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          font-size: 1.125rem;
          padding: 15px 30px;
        }
      }
    }
  }

  &__benefits {
    text-align: left;
    padding: 20px 20px;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      padding: 20px 40px;
    }
  }

  &__price {
    padding: 10px 20px;
    background-color: #ECF2F6;
    text-align: center;
    font-size: 1rem;
    font-weight: 700;
    border-radius: 0 0 20px 20px;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      font-size: 1.5rem;
      border-radius: 0 0 40px 40px;
      padding: 20px 40px;
    }
  }

  &.selected {
    position: relative;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 20px;
      border: 2px solid #2B257E;
      pointer-events: none;
      z-index: 4;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        border-radius: 40px;
      }
    }

    &.small:after {
      border-radius: 10px;
    }
  }

  &.disabled {
    position: relative;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 20px;
      background-color: rgba(255, 255, 255, .7);
      z-index: 4;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        border-radius: 40px;
      }
    }

    &.small:after {
      border-radius: 10px;
    }
  }
}
</style>
